/**
 * @file        src/pages/account/ActivityLog.jsx
 * @description Account Management → Activity Log. Every action taken in this
 *              console, newest first: who, what, which record, when, from where.
 *
 *              THE HISTORY ALREADY EXISTED; NOTHING COULD READ IT. `audit_logs`
 *              has been written from ~80 call sites in admin_backend since
 *              2026-07-27 — wallet credits, refund transitions, tenant
 *              activations, corporate approvals, staff and gateway changes,
 *              logins — and the only screen reading it was the Wallet Recharge
 *              Log, hard-filtered to the `wallet.` family. So the console kept a
 *              complete trail that an admin could not see a single row of unless
 *              it concerned a wallet.
 *
 *              ACTIONS ARE NAMED, NEVER INVENTED. The family filter is built
 *              from the families actually present in this tenant's table (the
 *              same rule Payment Reconciliation's reason filter follows), so it
 *              never offers a dead option — and an action this screen has no
 *              friendly wording for is shown VERBATIM rather than tidied into a
 *              neighbour's label. A trail that renames what it does not
 *              recognise is worse than one that shows the raw string: the day a
 *              new action starts being written, it has to be visible.
 *
 *              tenant_admin / super_admin only (the route enforces it). A staff
 *              member's own work is on the Action Centre.
 * @author      Kinetika Labs
 * @created     2026-09-24
 * @changelog
 *   - 2026-09-25 Claude — Details column wraps within a bounded width
 *                          (.activity-details) instead of running past the IP
 *                          column and off the table on a long email / company name.
 *   - 2026-09-24 Claude — Initial.
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Select from '../../components/Select';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { qk } from '../../api/queryKeys';
import { listActivity, activityFamilies } from '../../api/activityLog';
import { formatDateTime } from '../../lib/format';

const PAGE_SIZE = 25;
const URL_KEYS = ['actionFamily', 'search', 'from', 'to'];

// The verb an action ends in, coloured by what it DID. Read off the last
// segment, so a family nobody has taught this screen about still gets a sane
// tone — and the action string itself is always printed beside it.
const TONE_RULES = [
  [/(delete|remove|reject|suspend|freeze|decline|fail)/i, 'rej'],
  [/(credit|refund|approve|activate|enable|create|settle|complete)/i, 'ok'],
  [/(update|edit|modify|change|set)/i, 'pend'],
];
const toneFor = (action) => (TONE_RULES.find(([re]) => re.test(action))?.[1]) || 'warn';

/** `wallet.credit` → "Wallet · credit". Cosmetic only; never replaces the string. */
function prettyAction(action) {
  const [family, ...rest] = String(action || '').split('.');
  if (!rest.length) return action;
  const head = family.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
  return `${head} · ${rest.join('.').replace(/_/g, ' ')}`;
}

/** The interesting half of `meta`, flattened to one line. */
function metaSummary(meta) {
  if (!meta || typeof meta !== 'object') return '';
  const parts = [];
  for (const [k, v] of Object.entries(meta)) {
    if (v === null || v === undefined || v === '') continue;
    if (typeof v === 'object') continue;
    parts.push(`${k.replace(/_/g, ' ')}: ${v}`);
    if (parts.length >= 4) break;
  }
  return parts.join(' · ');
}

export default function ActivityLog() {
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [searchDraft, setSearchDraft] = useState(params.get('search') || '');

  const filters = {};
  for (const k of URL_KEYS) {
    const v = params.get(k) || '';
    if (v) filters[k] = v;
  }

  function setFilters(patch) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v); else next.delete(k);
    }
    setParams(next, { replace: true });
    setPage(1);
  }

  const { data: familyData } = useQuery({
    queryKey: qk.activityLog.families,
    queryFn: () => activityFamilies().then((r) => r.data),
    staleTime: 300_000,
  });
  const familyOptions = [
    { value: '', label: 'All activity' },
    ...(familyData?.families || []).map((f) => ({
      value: f,
      label: f.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()),
    })),
  ];

  const queryParams = { ...filters, page, pageSize: PAGE_SIZE };
  const { data, isLoading } = useQuery({
    queryKey: qk.activityLog.list(queryParams),
    queryFn: () => listActivity(queryParams),
    placeholderData: (prev) => prev,
    staleTime: 15_000,
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;
  const anyFilter = URL_KEYS.some((k) => params.get(k));

  return (
    <>
      <PageHead
        title="Activity Log"
        subtitle="Every action taken in this console, newest first"
      />

      <article className="panel glass">
        <form
          className="search-row"
          onSubmit={(e) => { e.preventDefault(); setFilters({ search: searchDraft }); }}
        >
          <div className="field" style={{ flex: '2 1 240px' }}>
            <label>Search</label>
            <input
              placeholder="Action, admin, or record id…"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
            />
          </div>
          <div className="field">
            <label>Area</label>
            <Select
              value={filters.actionFamily || ''}
              onChange={(v) => setFilters({ actionFamily: v })}
              options={familyOptions}
            />
          </div>
          <div className="field">
            <label>From</label>
            <input type="date" value={filters.from || ''} onChange={(e) => setFilters({ from: e.target.value })} />
          </div>
          <div className="field">
            <label>To</label>
            <input type="date" value={filters.to || ''} onChange={(e) => setFilters({ to: e.target.value })} />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
          <button
            type="button"
            className="btn ghost"
            disabled={!anyFilter}
            onClick={() => { setSearchDraft(''); setParams(new URLSearchParams(), { replace: true }); setPage(1); }}
          >
            Clear
          </button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Action</th>
                  <th>Record</th>
                  <th>Details</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                      {anyFilter ? 'No activity matches these filters.' : 'No activity recorded yet.'}
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.createdAt)}</td>
                    <td>
                      <b>{r.actorName}</b>
                      {r.actorRole ? <div className="muted" style={{ fontSize: '.68rem' }}>{r.actorRole}</div> : null}
                    </td>
                    <td>
                      <Chip tone={toneFor(r.action)}>{prettyAction(r.action)}</Chip>
                      {/* The raw string, always — see the file header. */}
                      <div className="muted mono" style={{ fontSize: '.66rem', marginTop: 3 }}>{r.action}</div>
                    </td>
                    <td>
                      {r.targetType
                        ? <span className="mono">{r.targetType}{r.targetId ? ` #${r.targetId}` : ''}</span>
                        : <span className="muted">—</span>}
                    </td>
                    {/* Free text (emails, company names, reasons) — it WRAPS inside a
                        bounded column. `.table tbody td` is nowrap for every list, and
                        maxWidth alone cannot stop an unbreakable string, so a long email
                        ran across the IP column and off the table. */}
                    <td className="muted activity-details">
                      {metaSummary(r.meta) || '—'}
                    </td>
                    <td className="mono muted" style={{ fontSize: '.68rem', whiteSpace: 'nowrap' }}>{r.ipAddress || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <Pager page={page} pageSize={PAGE_SIZE} total={total} onPage={setPage} />
      </article>
    </>
  );
}
